import { useState, useContext, useEffect, useMemo, useCallback, useRef } from "react";
import { CardOne } from "../../components/cards/cardOne";
import { GoBackButton } from "../../components/buttons/goBack";
import { themeContext } from "../../context/theme";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faUser,
  faCrown,
  faCheck,
  faClock,
  faPlay,
  faUsers,
  faGamepad,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./RoomLobby.module.css";
import { useNetworkService } from "../../hooks/useNetworkService";
import { OnlineArena } from "./OnlineArena";
import { CompactGameInfo } from "./CompactGameInfo";
import { ConfirmationModal } from "../../components/modals/ConfirmationModal";
import { useGamepad } from "../../hooks/useGamepad";
import { focusOnMouseMove, playSound } from "../../utilities/utilities";

export const RoomLobby = ({
  roomData,
  onBack,
  onCopyCode,
  playerName,
  game,
}) => {
  const [isReady, setIsReady] = useState(false);
  const [copied, setCopied] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameRoomData, setGameRoomData] = useState(null);
  const [showExitModal, setShowExitModal] = useState(false);
  const [isUIReady, setIsUIReady] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const lastModalCloseTime = useRef(0);
  const isMouseModeRef = useRef(false);
  const mainCardRef = useRef(null);
  const { theme } = useContext(themeContext);
  const themeNumber = theme.replace("theme-", "");

  // Focusable elements for keyboard/gamepad navigation
  const focusableElements = useMemo(() => {
    return { elements: null, index: 0 };
  }, []);

  // Use networking service for real-time updates
  const {
    playerId,
    toggleReady,
    startGame,
    updateGameSettings,
    isLoading,
    isConnected,
  } = useNetworkService();

  // Debug logging

  // Listen for game started event
  useEffect(() => {
    const handleGameStarted = (data) => {
      setGameRoomData(data.room);
      setGameStarted(true);
    };

    const networkService = require("../../services/NetworkService").default;
    networkService.on("game_started", handleGameStarted);

    return () => {
      networkService.off("game_started", handleGameStarted);
    };
  }, [playerId]);

  // Sync local ready state with server state from roomData
  useEffect(() => {
    if (roomData?.players && playerId) {
      const currentPlayer = roomData.players.find((p) => p.id === playerId);
      if (currentPlayer && currentPlayer.isReady !== isReady) {
        setIsReady(currentPlayer.isReady);
      }
    }
  }, [roomData?.players, playerId, isReady]);

  // Debug game settings changes
  useEffect(() => {
  }, [roomData?.gameSettings]);

  // Wait for UI animation to complete before accepting gamepad input
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsUIReady(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Initialize focusable elements and focus the main container
  useEffect(() => {
    if (!showExitModal && !gameStarted && !isSettingsModalOpen) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        const allElements = document.querySelectorAll("[tabindex='0']");
        // Filter out disabled elements AND the back button
        const elements = Array.from(allElements).filter(el => {
          return !el.disabled && !el.className.includes('go-back-button');
        });
        focusableElements.elements = elements;
        if (elements.length > 0) {
          // Set index but focus the main container instead of the first element
          focusableElements.index = 0;
          // Focus the main card container to enable keyboard events
          if (mainCardRef.current) {
            mainCardRef.current.focus();
          }
          isMouseModeRef.current = false;
        }
      }, 100);
    }
  }, [showExitModal, gameStarted, isSettingsModalOpen]);

  // Handle gamepad navigation
  const handleGamepadNavigation = useCallback((direction) => {
    // Switch to keyboard mode
    isMouseModeRef.current = false;

    const allElements = document.querySelectorAll("[tabindex='0']");
    // Filter out disabled elements AND the back button (we handle that with B button)
    const elements = Array.from(allElements).filter(el => {
      return !el.disabled && !el.className.includes('go-back-button');
    });
    if (elements.length === 0) return;

    focusableElements.elements = elements;

    if (focusableElements.index === undefined || focusableElements.index >= elements.length) {
      focusableElements.index = 0;
    }

    if (direction === 'down') {
      if (focusableElements.index >= elements.length - 1) {
        focusableElements.index = 0;
      } else {
        focusableElements.index++;
      }
    } else if (direction === 'up') {
      if (focusableElements.index <= 0) {
        focusableElements.index = elements.length - 1;
      } else {
        focusableElements.index--;
      }
    }

    if (elements[focusableElements.index]) {
      playSound(game, "navigation");
      elements[focusableElements.index].focus();
    }
  }, [focusableElements, game]);

  // Handle gamepad button presses
  const handleGamepadButtonPress = useCallback((button) => {
    // Prevent any button press if a modal was just closed
    const now = Date.now();
    if (now - lastModalCloseTime.current < 500) {
      return;
    }

    if (button === 'X') {
      // Use already-stored elements from navigation
      if (focusableElements.elements && focusableElements.elements[focusableElements.index]) {
        focusableElements.elements[focusableElements.index].click();
        game?.sounds?.clickSound?.play();
      }
    } else if (button === 'B') {
      // Toggle the exit modal
      setShowExitModal(prev => !prev);
      game?.sounds?.clickSound?.play();
    }
  }, [game, focusableElements]);

  // Use the useGamepad hook
  useGamepad({
    onNavigate: handleGamepadNavigation,
    onButtonPress: handleGamepadButtonPress,
    navigationDelay: 150,
    buttonDelay: 150,
    enabled: isUIReady && !showExitModal && !gameStarted && !isSettingsModalOpen,
    gamepadIndex: null
  });

  // If game has started, show the arena
  if (gameStarted && gameRoomData) {
    return (
      <OnlineArena
        roomData={gameRoomData}
        onBack={() => {
          // Reset game state and return to lobby (don't exit room)
          setGameStarted(false);
          setGameRoomData(null);
          setIsReady(false); // Reset ready status
          // Don't call onBack() - stay in the room lobby
        }}
        game={game}
      />
    );
  }

  const handleCopyCode = () => {
    onCopyCode();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleToggleReady = async () => {
    if (!playerId) {
      return;
    }

    const newReadyState = !isReady;
    setIsReady(newReadyState);
    game?.sounds?.clickSound?.play();

    // Blur the button and refocus the main container to maintain keyboard navigation
    if (document.activeElement) {
      document.activeElement.blur();
    }
    // Refocus main container to keep keyboard navigation working
    if (mainCardRef.current) {
      mainCardRef.current.focus();
    }

    try {
      await toggleReady(newReadyState);
    } catch (error) {
      // Revert on error
      setIsReady(!newReadyState);
    }
  };

  const handleStartGame = async () => {
    game?.sounds?.clickSound?.play();

    try {
      await startGame();
    } catch (error) {
    }
  };

  const handleUpdateGameSettings = async (newSettings) => {
    try {
      await updateGameSettings(newSettings);
    } catch (error) {
    }
  };

  // Find current player by ID only
  const currentPlayer = roomData?.players?.find((p) => p.id === playerId);

  const isHost = currentPlayer?.isHost || false;

  // Use roomData as the source of truth for all players, including current player
  // This ensures consistency when server updates come in
  const allPlayersReady =
    roomData?.players?.every((p) => {
      // For current player, prefer server state from roomData if available, otherwise use local state
      if (p.id === playerId) {
        return currentPlayer?.isReady !== undefined
          ? currentPlayer.isReady
          : isReady;
      }
      return p.isReady;
    }) || false;

  const canStartGame =
    isHost && allPlayersReady && (roomData?.players?.length || 0) >= 2;

  return (
    <div className={styles.pageContainer}>
      <CardOne
        customTag="section"
        attributes={{
          ref: mainCardRef,
          className: styles.mainCard,
          onKeyDown: (event) => {
            if (event.key === 'Escape' && !isSettingsModalOpen) {
              event.preventDefault();
              setShowExitModal(prev => !prev);
              game?.sounds?.clickSound?.play();
              return;
            }

            if (!showExitModal && !gameStarted && !isSettingsModalOpen) {
              // Switch to keyboard mode
              isMouseModeRef.current = false;

              const allElements = document.querySelectorAll("[tabindex='0']");
              // Filter out disabled elements AND the back button
              const elements = Array.from(allElements).filter(el => {
                return !el.disabled && !el.className.includes('go-back-button');
              });
              if (elements.length > 0) {
                focusableElements.elements = elements;

                if (focusableElements.index === undefined || focusableElements.index >= elements.length) {
                  focusableElements.index = 0;
                }

                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  if (focusableElements.index >= elements.length - 1) {
                    focusableElements.index = 0;
                  } else {
                    focusableElements.index++;
                  }
                  if (elements[focusableElements.index]) {
                    playSound(game, "navigation");
                    elements[focusableElements.index].focus();
                  }
                } else if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  if (focusableElements.index <= 0) {
                    focusableElements.index = elements.length - 1;
                  } else {
                    focusableElements.index--;
                  }
                  if (elements[focusableElements.index]) {
                    playSound(game, "navigation");
                    elements[focusableElements.index].focus();
                  }
                } else if (event.key === 'Enter') {
                  event.preventDefault();
                  if (elements[focusableElements.index]) {
                    elements[focusableElements.index].click();
                  }
                }
              }
            }
          },
          onMouseMove: (event) => {
            if (!showExitModal && !gameStarted && !isSettingsModalOpen && isMouseModeRef.current !== false) {
              // Only process if not in keyboard mode
              // Switch to mouse mode
              isMouseModeRef.current = true;

              const allElements = document.querySelectorAll("[tabindex='0']");
              // Filter out disabled elements AND the back button
              const elements = Array.from(allElements).filter(el => {
                return !el.disabled && !el.className.includes('go-back-button');
              });
              if (elements.length > 0) {
                focusableElements.elements = elements;
                // When hovering a new element, blur ALL focused elements (mouse mode)
                focusableElements?.elements?.forEach((element, index) => {
                  if (event.target === element && index !== focusableElements.index) {
                    // Blur all elements when in mouse mode
                    focusableElements.elements.forEach(el => {
                      if (el === document.activeElement) {
                        el.blur();
                      }
                    });
                    focusableElements.index = index;
                  }
                });
              }
            }
          },
          tabIndex: -1
        }}
      >
        <GoBackButton
          onClickHandler={() => setShowExitModal(true)}
          game={game}
        />

        <div className={styles.container}>
          {/* Room Header */}
          <div className={styles.header}>
            <div className={styles.roomInfo}>
              <h1 className={styles.title}>
                <FontAwesomeIcon icon={faUsers} className={styles.titleIcon} />
                Room Lobby
              </h1>
              <div className={styles.roomCodeSection}>
                <span className={styles.roomCodeLabel}>Room Code:</span>
                <div className={styles.roomCodeContainer}>
                  <span
                    className={`${styles.roomCode} ${
                      styles[`roomCode-theme-${theme}`]
                    }`}
                  >
                    {roomData?.code || "Loading..."}
                  </span>
                  <button
                    className={styles.copyButton}
                    onClick={handleCopyCode}
                  >
                    <FontAwesomeIcon icon={faCopy} />
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Compact Game Info */}
          <CompactGameInfo
            key={`gameinfo-${JSON.stringify(roomData?.gameSettings)}`}
            gameSettings={roomData?.gameSettings || {}}
            onUpdateSettings={handleUpdateGameSettings}
            isHost={isHost}
            game={game}
            isLoading={isLoading}
            onModalStateChange={setIsSettingsModalOpen}
            onModalClosed={() => {
              lastModalCloseTime.current = Date.now();
            }}
          />

          {/* Players List */}
          <div className={styles.playersSection}>
            <h2 className={styles.sectionTitle}>
              Players ({roomData?.players?.length || 0}/
              {roomData?.maxPlayers || 4})
            </h2>

            <div className={styles.playersList}>
              {roomData?.players?.map((player) => (
                <div
                  key={player.id}
                  className={`${styles.playerCard} ${
                    styles[`playerCard-theme-${theme}`]
                  }`}
                >
                  <div className={styles.playerInfo}>
                    <FontAwesomeIcon
                      icon={faUser}
                      className={styles.playerIcon}
                    />
                    <span className={styles.playerName}>
                      {player.name}
                      {player.id === playerId && " (You)"}
                    </span>
                    {player.isHost && (
                      <FontAwesomeIcon
                        icon={faCrown}
                        className={styles.hostIcon}
                        title="Host"
                      />
                    )}
                  </div>

                  <div className={styles.playerStatus}>
                    {player.id === playerId ? (
                      <div
                        className={`${styles.readyStatus} ${
                          (
                            currentPlayer?.isReady !== undefined
                              ? currentPlayer.isReady
                              : isReady
                          )
                            ? styles.ready
                            : styles.notReady
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={faCheck}
                          className={styles.statusIcon}
                        />
                      </div>
                    ) : (
                      <div
                        className={`${styles.readyStatus} ${
                          player.isReady ? styles.ready : styles.notReady
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={faCheck}
                          className={styles.statusIcon}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Empty slots */}
              {Array.from({
                length:
                  (roomData?.maxPlayers || 4) -
                  (roomData?.players?.length || 0),
              }).map((_, index) => (
                <div
                  key={`empty-${index}`}
                  className={`${styles.playerCard} ${styles.emptySlot}`}
                >
                  <div className={styles.playerInfo}>
                    <FontAwesomeIcon
                      icon={faUser}
                      className={styles.playerIconEmpty}
                    />
                    <span className={styles.emptySlotText}>
                      Waiting for player...
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>


          {/* Action Buttons */}
          <div className={styles.actionsContainer}>
            <button
              className={`${styles.readyButton} ${
                styles[`readyButton-theme-${themeNumber}`]
              } ${
                (
                  currentPlayer?.isReady !== undefined
                    ? currentPlayer.isReady
                    : isReady
                )
                  ? styles.readyActive
                  : ""
              }`}
              onClick={handleToggleReady}
              disabled={isLoading}
              tabIndex="0"
            >
              <FontAwesomeIcon
                icon={
                  (
                    currentPlayer?.isReady !== undefined
                      ? currentPlayer.isReady
                      : isReady
                  )
                    ? faCheck
                    : faClock
                }
                className={styles.buttonIcon}
              />
              {(
                currentPlayer?.isReady !== undefined
                  ? currentPlayer.isReady
                  : isReady
              )
                ? "Ready!"
                : "Ready Up"}
            </button>

            {isHost && (
              <button
                className={`${styles.startButton} ${
                  styles[`startButton-theme-${themeNumber}`]
                }`}
                onClick={handleStartGame}
                disabled={!canStartGame || isLoading}
                tabIndex="0"
              >
                <FontAwesomeIcon icon={faPlay} className={styles.buttonIcon} />
                Start Game
              </button>
            )}
          </div>

        </div>
      </CardOne>

      {/* Exit Room Confirmation Modal */}
      <ConfirmationModal
        isVisible={showExitModal}
        title="Exit Room"
        message="Are you sure you want to exit the room? You will lose your spot and any progress."
        confirmText="Exit Room"
        cancelText="Stay"
        confirmButtonStyle="danger"
        onConfirm={() => {
          setShowExitModal(false);
          lastModalCloseTime.current = Date.now();
          onBack();
        }}
        onCancel={() => {
          setShowExitModal(false);
          lastModalCloseTime.current = Date.now();
        }}
      />
    </div>
  );
};
